// DEPLOY-PROBE-bump

/**
 * ControlsSection Component
 *
 * Control panel for construction worker payslip preview (給与 / 賞与).
 */

import React from 'react';
import { filterConstructionBonusMonths } from '../../../../utils/constructionBonusContract';
import { SettingsIcon } from './SettingsIcon';
import { AutocompleteInput } from './AutocompleteInput';

interface ControlsSectionProps {
  payslipKind: 'salary' | 'bonus';
  selectedYear: string;
  selectedMonth: string;
  selectedPeriod: string;
  empName: string;
  selectedSite: string;
  availableYears: number[];
  availableMonths: number[];
  availablePeriods: string[];
  availableSites: string[];
  onPayslipKindChange: (kind: 'salary' | 'bonus') => void;
  onYearChange: (year: string) => void;
  onMonthChange: (month: string) => void;
  onPeriodChange: (period: string) => void;
  onEmpNameChange: (name: string) => void;
  onEmpNameSelect?: (name: string) => void;
  onSiteChange: (site: string) => void;
  onClear: () => void;
  onPdfDownload: () => void;
  onSettingsClick: () => void;
  isEmployeeView?: boolean;
  siteSelectLocked?: boolean;
  hideBonusTab?: boolean;
}

export const ControlsSection: React.FC<ControlsSectionProps> = ({
  payslipKind,
  selectedYear,
  selectedMonth,
  selectedPeriod,
  empName,
  selectedSite,
  availableYears = [],
  availableMonths = [],
  availablePeriods = [],
  availableSites = [],
  onPayslipKindChange,
  onYearChange,
  onMonthChange,
  onPeriodChange,
  onEmpNameChange,
  onEmpNameSelect,
  onSiteChange,
  onClear,
  onPdfDownload,
  onSettingsClick,
  isEmployeeView = false,
  siteSelectLocked = false,
  hideBonusTab = false,
}) => {
  const years = (availableYears ?? []).length > 0 ? availableYears : [new Date().getFullYear()];
  const months =
    payslipKind === 'salary' && (availableMonths ?? []).length > 0
      ? availableMonths
      : Array.from({ length: 12 }, (_, i) => i + 1);
  const periods =
    payslipKind === 'bonus'
      ? filterConstructionBonusMonths(availablePeriods ?? [])
      : [];

  const pdfReady =
    Boolean(selectedYear && empName) &&
    (payslipKind === 'salary'
      ? Boolean(selectedMonth && selectedSite)
      : Boolean(selectedPeriod));

  return (
    <div className="controls-section">
      <div className="data-type-toggle">
        <button
          type="button"
          className={`toggle-btn ${payslipKind === 'salary' ? 'active' : ''}`}
          onClick={() => onPayslipKindChange('salary')}
        >
          給与データ
        </button>
        {!hideBonusTab && (
          <button
            type="button"
            className={`toggle-btn ${payslipKind === 'bonus' ? 'active' : ''}`}
            onClick={() => onPayslipKindChange('bonus')}
          >
            賞与データ
          </button>
        )}
      </div>

      <div className="input-row">
        <div className="input-group">
          {payslipKind === 'salary' && (
            <div className="site-field">
              <label htmlFor={siteSelectLocked ? 'site-select-locked' : 'site-select'}>現場:</label>
              {siteSelectLocked ? (
                <span id="site-select-locked" className="site-select site-select-locked" title={selectedSite}>
                  {selectedSite || '—'}
                </span>
              ) : (
                <select
                  id="site-select"
                  value={selectedSite}
                  onChange={(e) => onSiteChange(e.target.value)}
                  className="site-select"
                >
                  <option value="">現場を選択</option>
                  {(availableSites ?? []).map((site) => (
                    <option key={site} value={site}>
                      {site}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="date-field">
            <label htmlFor="year-select">年:</label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => onYearChange(e.target.value)}
              className="date-select"
            >
              <option value="">年を選択</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {payslipKind === 'salary' ? (
            <div className="date-field">
              <label htmlFor="month-select">月:</label>
              <select
                id="month-select"
                value={selectedMonth}
                onChange={(e) => onMonthChange(e.target.value)}
                className="date-select"
              >
                <option value="">月を選択</option>
                {months.map((month) => (
                  <option key={month} value={month}>
                    {month}月
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="date-field">
              <label htmlFor="bonus-month-select">月:</label>
              <select
                id="bonus-month-select"
                value={selectedPeriod}
                onChange={(e) => onPeriodChange(e.target.value)}
                className="date-select"
              >
                <option value="">月を選択</option>
                {periods.map((period) => (
                  <option key={period} value={period}>
                    {period}月
                  </option>
                ))}
              </select>
            </div>
          )}

          {!isEmployeeView && (
            <div className="emp-name-field">
              <label htmlFor="emp-name">従業員名:</label>
              <AutocompleteInput
                id="emp-name"
                value={empName}
                onChange={onEmpNameChange}
                onSelect={onEmpNameSelect}
                placeholder="従業員名を入力"
              />
            </div>
          )}

          <div className="button-container">
            <button type="button" className="clear-button" onClick={onClear}>
              クリア
            </button>
            <button type="button" className="pdf-button" onClick={onPdfDownload} disabled={!pdfReady}>
              PDF出力
            </button>
            {!isEmployeeView && <SettingsIcon onClick={onSettingsClick} />}
          </div>
        </div>
      </div>
    </div>
  );
};
